import { DownOutlined, RedoOutlined, UpOutlined } from '@ant-design/icons';
import { IssueAmountStructure, IssueProduct } from '@gooduncles/gu-app-schema';
import { Button, InputNumber, notification } from 'antd';
import { FC, useEffect, useState } from 'react';

import { errorObjectToString, formatNumber, parseKoreanCurrency } from 'src/utils/common';
import { calculateIssueAmount } from 'src/utils/issue/issue-util';

import classes from './IssueAmount.module.scss';

interface IssueAmountProps {
  issueProducts: IssueProduct[];
  issueAmount: IssueAmountStructure | null;
  onAmountUpdate?: (amount: IssueAmountStructure) => Promise<void>;
  /** 환불 Task 처럼 금액을 음수로만 입력해야 하는 경우 true */
  negativeOnly?: boolean;
}

const IssueAmount: FC<IssueAmountProps> = ({ issueProducts, issueAmount, onAmountUpdate, negativeOnly = false }) => {
  const isEditable = Boolean(onAmountUpdate);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingAmount, setEditingAmount] = useState<IssueAmountStructure>(
    issueAmount ?? { totalAmount: 0, supplyAmount: 0, taxAmount: 0 },
  );

  // 환불 Task 등 음수 전용인 경우 입력값을 항상 음수로 보정
  const normalizeValue = (value: number) => (negativeOnly ? -Math.abs(value) : value);

  const handleReset = () => {
    const originalAmount = calculateIssueAmount(issueProducts);
    setEditingAmount(originalAmount);
  };

  const handleAmountChange = (field: keyof IssueAmountStructure, value: number | null) => {
    if (value === null) return;

    // 환불 Task는 음수만 입력 가능 - 양수 입력 시 안내
    if (negativeOnly && value > 0) {
      notification.warning({
        message: '환불 금액은 음수로만 입력할 수 있습니다.',
        description: '입력하신 금액은 자동으로 음수로 변환됩니다.',
      });
    }

    // 공급가나 세금이 변경되면 총액을 자동으로 계산
    const newAmount = { ...editingAmount, [field]: normalizeValue(value) };
    if (field === 'supplyAmount' || field === 'taxAmount') {
      newAmount.totalAmount = newAmount.supplyAmount + newAmount.taxAmount;
    }
    setEditingAmount(newAmount);
  };

  const handleAmountUpdate = async () => {
    if (!onAmountUpdate) return;

    setLoading(true);
    try {
      await onAmountUpdate(editingAmount);
    } catch (error) {
      console.error(error);
      notification.error({
        message: '금액 수정에 실패했습니다.',
        description: errorObjectToString(error),
      });
    }
    setLoading(false);
  };

  // 원본 금액과 현재 편집 중인 금액이 동일한지 확인
  const isAmountChanged = () => {
    if (!issueAmount) return true;
    return (
      editingAmount.supplyAmount !== issueAmount.supplyAmount ||
      editingAmount.taxAmount !== issueAmount.taxAmount ||
      editingAmount.totalAmount !== issueAmount.totalAmount
    );
  };

  useEffect(() => {
    setEditingAmount(issueAmount ?? { totalAmount: 0, supplyAmount: 0, taxAmount: 0 });
  }, [issueAmount]);

  return (
    <>
      <div className={classes.infoItem} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={classes.label}>
          총 금액
          {isExpanded ? <UpOutlined className={classes.toggleIcon} /> : <DownOutlined className={classes.toggleIcon} />}
        </div>
        <div className={classes.value}>{formatNumber(editingAmount.supplyAmount + editingAmount.taxAmount)}원</div>
      </div>
      {isExpanded && (
        <div className={classes.expandedContainer}>
          <div className={classes.amountFields}>
            <div className={classes.amountItem}>
              <div className={classes.label}>공급가</div>
              <div className={classes.inputWrapper}>
                <InputNumber
                  size='small'
                  step={100}
                  value={editingAmount.supplyAmount}
                  onChange={isEditable ? (value) => handleAmountChange('supplyAmount', value) : undefined}
                  formatter={(value) => formatNumber(value ?? 0) + ' 원'}
                  parser={parseKoreanCurrency}
                  className={classes.amountInput}
                  disabled={!isEditable}
                />
              </div>
            </div>

            <div className={classes.amountItem}>
              <div className={classes.label}>세금</div>
              <div className={classes.inputWrapper}>
                <InputNumber
                  size='small'
                  step={100}
                  value={editingAmount.taxAmount}
                  onChange={isEditable ? (value) => handleAmountChange('taxAmount', value) : undefined}
                  formatter={(value) => formatNumber(value ?? 0) + ' 원'}
                  parser={parseKoreanCurrency}
                  className={classes.amountInput}
                  disabled={!isEditable}
                />
              </div>
            </div>
          </div>

          {isEditable && (
            <div className={classes.footer}>
              <Button
                type='default'
                size='small'
                loading={loading}
                icon={<RedoOutlined />}
                onClick={handleReset}
                className={classes.footerButton}
              >
                초기화
              </Button>
              <Button
                type='primary'
                size='small'
                loading={loading}
                onClick={handleAmountUpdate}
                disabled={!isAmountChanged()}
                className={classes.footerButton}
              >
                금액 수정
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default IssueAmount;
export type { IssueAmountProps };
